/**
 * useAddressGuard
 *
 * Checks whether the currently logged-in user has a delivery address on their
 * profile.  If not, it shows the AddressRequiredModal before executing the
 * requested action.
 *
 * Usage:
 *   const { AddressModal, guardAction } = useAddressGuard();
 *   // Render <AddressModal /> somewhere in your JSX tree.
 *   // Then wrap any action that requires an address:
 *   guardAction(() => addToCart(shirt));
 */
import React, { useCallback, useState } from "react";
import { userService } from "../services/userService";
import AddressRequiredModal from "../components/AddressRequiredModal/AddressRequiredModal";

function hasAddress(profile: Record<string, unknown>): boolean {
  const street = typeof profile.street === "string" ? profile.street.trim() : "";
  const zip    = typeof profile.zip    === "string" ? profile.zip.trim()    : "";
  const city   = typeof profile.city   === "string" ? profile.city.trim()   : "";
  return street.length > 0 && zip.length > 0 && city.length > 0;
}

export function useAddressGuard() {
  const [showModal, setShowModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  /**
   * Run `action` immediately if the user already has an address.
   * Otherwise show the AddressRequiredModal first; when the user saves their
   * address the action fires automatically.
   */
  const guardAction = useCallback(async (action: () => void) => {
    try {
      const profile = await userService.fetchProfile();
      if (hasAddress(profile as Record<string, unknown>)) {
        action();
      } else {
        // Store action so it can be invoked after the modal saves
        setPendingAction(() => action);
        setShowModal(true);
      }
    } catch {
      // If fetching profile fails (e.g. network), allow the action so as not
      // to block the user on a connectivity issue.
      action();
    }
  }, []);

  const handleSaved = useCallback(() => {
    setShowModal(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  }, [pendingAction]);

  const handleClose = useCallback(() => {
    setShowModal(false);
    setPendingAction(null);
  }, []);

  /**
   * Drop this element anywhere inside the component's JSX.
   * It renders nothing when no address check is pending.
   */
  const AddressModal = showModal
    ? React.createElement(AddressRequiredModal, {
        onSaved: handleSaved,
        onClose: handleClose,
      })
    : null;

  return { guardAction, AddressModal };
}
